import ApiClient from 'api-client/ApiClient'
import { CqrsRole } from 'api-client/keystone.model'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import Bugsnag from 'utils/bugsnag'

const { keystone } = ApiClient.getInstance()

// Matches the body shape built by CreateCustomRoleModal/EditCustomRoleModal (a partial
// CqrsRole nested under a `role` key, per the /v1/roles POST/PUT payload).
interface CreateOrUpdateRoleBody {
  role: Partial<Pick<CqrsRole, 'name' | 'description' | 'permissions'>>
}

export const mngmRoleActions = ActionsSet.make<DataKeys.ManagementRoles>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.ManagementRoles,
  cacheKey: DataKeys.ManagementRoles,
})

export const listMngmRoles = mngmRoleActions.add(
  new ListAction<DataKeys.ManagementRoles>(async () => {
    Bugsnag.leaveBreadcrumb('Attempting to get management roles')
    return keystone.getRoles()
  }),
)

export const createRole = mngmRoleActions.add(
  new CreateAction<DataKeys.ManagementRoles, { body: CreateOrUpdateRoleBody }>(async ({ body }) => {
    const response = await keystone.createRole({ body })
    const roleId = response?.id
    const newRole = await keystone.getRole({ id: roleId })
    return newRole
  }),
)

export const updateRole = mngmRoleActions.add(
  new UpdateAction<DataKeys.ManagementRoles, { id: string; body: CreateOrUpdateRoleBody }>(
    async ({ id, body }) => {
      const response = await keystone.updateRole({ id, body })
      const updatedRole = await keystone.getRole({ id })
      return updatedRole
    },
  ),
)

export const capabilityActions = ActionsSet.make<DataKeys.Capabilities>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.Capabilities,
  cacheKey: DataKeys.Capabilities,
})

export const listCapabilities = capabilityActions.add(
  new ListAction<DataKeys.Capabilities>(async () => {
    Bugsnag.leaveBreadcrumb('Attempting to get role capabilities')
    return keystone.getCapabilitiesList()
  }),
)

export const getPermissionsList = async () => {
  return keystone.getPermissionsList()
}

export const getRolePermissions = async ({ id }) => {
  const role: CqrsRole = await keystone.getCqrsRole({ id })
  return role?.permissions ?? []
}

export const getCapabilitiesList = async () => {
  return keystone.getCapabilitiesList()
}
